import type { QuickReply } from '@dnx/contracts';
import { cn } from '@dnx/ui';
import { useQuery } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { quickRepliesQuery } from './queries.js';

export interface QuickReplyVars { nome: string; clinica: string; atendente: string }
export const fillVars = (body: string, v: QuickReplyVars) =>
  body.replace(/\{(nome|clinica|atendente)\}/g, (_m, k: keyof QuickReplyVars) => v[k]);

export interface QuickReplyMenuHandle { onKey: (e: React.KeyboardEvent) => boolean }

/**
 * Lista de respostas rápidas acima do campo: abre quando a mensagem começa com "/" (filtra pelo atalho
 * ou título). Setas escolhem, Enter/Tab insere, Esc fecha. Devolve true quando consumiu a tecla.
 */
export const QuickReplyMenu = forwardRef<QuickReplyMenuHandle, { text: string; vars: QuickReplyVars; onPick: (text: string) => void; forceOpen: boolean; onClose: () => void }>(
  function QuickReplyMenu({ text, vars, onPick, forceOpen, onClose }, ref) {
    const { data } = useQuery(quickRepliesQuery);
    const [active, setActive] = useState(0);
    const query = text.startsWith('/') ? text.slice(1).toLowerCase() : forceOpen ? '' : null;
    const items: QuickReply[] = query === null ? [] : (data ?? []).filter((q) => q.shortcut.includes(query) || q.title.toLowerCase().includes(query)).slice(0, 8);
    const open = query !== null && !text.includes('\n') && (items.length > 0 || forceOpen);
    const idx = Math.min(active, Math.max(0, items.length - 1));

    useImperativeHandle(ref, () => ({
      onKey: (e) => {
        if (!open) return false;
        if (e.key === 'ArrowDown') { setActive((idx + 1) % Math.max(1, items.length)); return true; }
        if (e.key === 'ArrowUp') { setActive((idx - 1 + items.length) % Math.max(1, items.length)); return true; }
        if ((e.key === 'Enter' || e.key === 'Tab') && items[idx]) { onPick(fillVars(items[idx]!.body, vars)); setActive(0); return true; }
        if (e.key === 'Escape') { onClose(); return true; }
        return false;
      },
    }), [open, idx, items, onPick, onClose, vars]);

    if (!open) return null;
    return (
      <div className="absolute right-0 bottom-full left-0 z-20 mb-2 overflow-hidden rounded-xl border border-border bg-surface shadow-pop" role="listbox" aria-label="Respostas rápidas">
        <p className="flex items-center gap-1.5 border-b border-border px-3 py-2 text-[11px] font-semibold tracking-[0.14em] text-fg-subtle uppercase">
          <Zap className="size-3.5" />Respostas rápidas · ↑↓ escolhe · Enter insere
        </p>
        {!items.length ? (
          <p className="px-3 py-3 text-[13px] text-fg-muted">Nenhuma resposta{query ? ` para “${query}”` : ''}. A gestão cria em Configurações → Respostas rápidas.</p>
        ) : items.map((q, i) => (
          <button key={q.id} type="button" role="option" aria-selected={i === idx} onMouseEnter={() => setActive(i)}
            onMouseDown={(e) => { e.preventDefault(); onPick(fillVars(q.body, vars)); }}
            className={cn('block w-full px-3 py-2 text-left', i === idx ? 'bg-primary-soft' : 'hover:bg-surface-muted')}>
            <span className="flex items-baseline gap-2">
              <code className="font-mono text-[12px] text-primary">/{q.shortcut}</code>
              <span className="truncate text-[13px] font-medium text-fg">{q.title}</span>
            </span>
            <span className="mt-0.5 block truncate text-[12px] text-fg-muted">{fillVars(q.body, vars)}</span>
          </button>
        ))}
      </div>
    );
  });
